import { AdvancedSelect } from '@orobix/orobix-fe-components'
import { useField, useFormikContext } from 'formik'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { SelectOption } from '../../../types'
import { BackboneSelectOption } from '../../../types/backbone'
import { useAnomalyStrategiesData } from './useAnomalyStrategiesData'
import { anomalyStrategyToOption } from './utils'

export interface PatchStrategySelectProps {
  fieldName: string
  selectedBackbone?: BackboneSelectOption[]
}

const AnomalyStrategySelect = ({
  fieldName,
  selectedBackbone,
}: PatchStrategySelectProps) => {
  const { t } = useTranslation()
  const { isSubmitting } = useFormikContext()
  const [field, meta, helpers] = useField<SelectOption>(fieldName)

  const { name, value } = field
  const { error, touched } = meta
  const { setValue, setTouched } = helpers

  const {
    anomalyStrategies,
    anomalyStrategyDft,
    anomalyStrategyIsLoading,
  } = useAnomalyStrategiesData()

  const toOption = anomalyStrategyToOption()

  useEffect(() => {
    if (!anomalyStrategyIsLoading && anomalyStrategyDft) {
      setValue(toOption(anomalyStrategyDft))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anomalyStrategyIsLoading, anomalyStrategyDft])

  const backboneWithAnomalyStrategyNotSupported = selectedBackbone
    ? selectedBackbone.filter(
        (b) => b.metadata.trainingAnomalyStrategySupported === false
      )
    : []

  return (
    <div id="divAnomalyStrategyId">
      <AdvancedSelect
        name={name}
        id={name}
        label={t('Anomaly strategy')}
        value={value}
        onChange={(v: SelectOption | null) => {
          if (v) setValue(v)
          setTouched(true)
        }}
        onBlur={() => setTouched(true)}
        options={anomalyStrategies ? anomalyStrategies.map(toOption) : []}
        error={!!(error && touched)}
        errorMessage={error}
        isDisabled={
          isSubmitting ||
          anomalyStrategyIsLoading ||
          (selectedBackbone &&
            selectedBackbone?.length > 0 &&
            selectedBackbone.length ===
              backboneWithAnomalyStrategyNotSupported.length)
        }
        helpMessage={
          selectedBackbone &&
          selectedBackbone.length >= 1 &&
          backboneWithAnomalyStrategyNotSupported.length > 0 &&
          selectedBackbone.length >
            backboneWithAnomalyStrategyNotSupported.length
            ? `${
                backboneWithAnomalyStrategyNotSupported.length === 1
                  ? t(
                      'The selected strategy is not applied to the training with the backbone'
                    )
                  : t(
                      'The selected strategy is not applied to the trainings with the backbones'
                    )
              }: ${backboneWithAnomalyStrategyNotSupported
                .map((b) => b.label.split(' [')[0])
                .join(', ')}.`
            : ''
        }
        noOptionsMessage={() => t('No anomaly strategy found')}
        placeholder={
          anomalyStrategyIsLoading ? t('Loading options...') : t('Select...')
        }
      />
    </div>
  )
}

export default AnomalyStrategySelect
